import axios from 'axios';

import { ChatData, ChatMessageAttachment, ChatMessageReaction, ChatUser } from '../../types';
import config from '../config';
import { axios as coreApiAxios } from '../';
import { callable } from '../firebase-session';
import { firebaseFunctionUrl, getFirebaseTokenRequest } from '../firebase-functions';

export { getFirebaseTokenRequest };

export const createCompanyChatRequest = async ({
  companyId,
  creator,
  name,
  description = '',
  avatarUrl = null,
  entityId = '',
  type,
  token,
}: {
  creator: ChatUser;
  name: string;
  companyId: string;
  description?: string;
  avatarUrl?: string | null;
  entityId?: string;
  type: 'company' | 'location' | 'team' | 'private';
  token: string;
}) => {
  return axios.post(
    firebaseFunctionUrl('createcompanychat'),
    {
      creator,
      name,
      companyId,
      type,
      description,
      avatarUrl,
      entityId,
    },
    { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
  );
};

export const syncAdminChatsRequest = ({
  companyId,
  entityIds,
  chatUser,
  token,
}: {
  companyId: string;
  entityIds: string[];
  chatUser: ChatUser;
  token: string;
}) => {
  return axios.post(
    firebaseFunctionUrl('syncadminchats'),
    {
      companyId,
      entityIds,
      user: chatUser,
    },
    { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
  );
};

export const updateChatRequest = async ({
  companyId,
  entityId,
  name,
  description,
  avatarUrl,
  token,
  userId,
  chatId,
}: {
  companyId: string;
  entityId?: string;
  name?: string;
  description?: string;
  avatarUrl?: string;
  token: string;
  userId: string;
  chatId?: string;
}) => {
  return axios.post(
    firebaseFunctionUrl('updatechat'),
    { companyId, entityId, name, description, avatarUrl, userId, chatId },
    { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
  );
};

export const removeUsersFromChatRequest = async ({
  companyId,
  entityId,
  userIds,
  chatId,
  token,
}: {
  companyId: string;
  entityId?: string;
  userIds: string[];
  chatId?: string;
  token: string;
}) => {
  return axios.post(
    firebaseFunctionUrl('removeusersfromchat'),
    { companyId, entityId, userIds, chatId },
    { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
  );
};

export const addUsersToChatRequest = async ({
  companyId,
  entityId,
  users,
  chatId,
  token,
}: {
  companyId: string;
  entityId?: string;
  users: ChatUser[];
  chatId?: string;
  token: string;
}) => {
  return axios.post(
    firebaseFunctionUrl('adduserstochat'),
    { companyId, entityId, users, chatId },
    { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
  );
};

export const removeChatRequest = async ({
  companyId,
  entityId,
  token,
  userId,
}: {
  companyId: string;
  entityId: string;
  token: string;
  userId: string;
}) => {
  return axios.post(
    firebaseFunctionUrl('removechat'),
    { companyId, entityId, userId },
    { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
  );
};

export const getUserChatsRequest = async ({
  uid,
  type,
  limit,
  lastChatId,
  companyId,
  searchQuery,
  unreadOnly,
  slim,
}: {
  uid: string;
  type?: 'private' | 'group' | null;
  limit: number;
  companyId: string;
  lastChatId?: string;
  searchQuery?: string;
  /** When true, only chats with unread messages for this user are returned (server-side filter). */
  unreadOnly?: boolean;
  /** Group chats come without member profiles; open the chat (getChat) for those. */
  slim?: boolean;
}): Promise<{ chats: ChatData[]; lastChatId?: string; hasMore?: boolean }> => {
  const fn = callable('getUserChats');
  const result = await fn({ uid, type, limit, lastChatId, companyId, searchQuery, unreadOnly, slim });

  return result.data as { chats: ChatData[]; lastChatId?: string; hasMore?: boolean };
};

export interface UnreadMessagesCountResponse {
  totalUnreadCount: number;
  companyUnreadCounts: {
    companyId: string;
    totalUnreadCount: number;
    chatUnreadCounts: { chatId: string; chatName: string; chatType: string; unreadCount: number }[];
  }[];
}

/** Total unread messages for the user across the given companies (companies/{id}/chats). */
export const getAllUnreadMessagesCountRequest = async ({
  userId,
  companyIds,
}: {
  userId: string;
  companyIds: string[];
}): Promise<UnreadMessagesCountResponse> => {
  const fn = callable('getAllUnreadMessagesCount');
  const result = await fn({ userId, companyIds });
  return result.data as UnreadMessagesCountResponse;
};

export const getChatRequest = async ({
  chatId,
  userId,
  companyId,
}: {
  chatId: string;
  userId: string;
  companyId: string;
}) => {
  const fn = callable('getChat');
  const result = await fn({ chatId, userId, companyId });
  return result.data as ChatData;
};

export const uploadImageRequest = async ({ base64Data, token }: { base64Data: string; token: string }) => {
  return axios.post(
    firebaseFunctionUrl('uploadimage'),
    {
      base64Data,
    },
    { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
  );
};

export const updateChatReadOnlyStatusRequest = async ({
  chatId,
  readOnly,
  companyId,
  userId,
}: {
  chatId: string;
  readOnly: boolean;
  companyId: string;
  userId: string;
}) => {
  const fn = callable('updateChatReadOnlyStatus');
  const result = await fn({ chatId, readOnly, companyId, userId });
  return result.data;
};

export const markMessagesAsReadRequest = async ({
  companyId,
  chatId,
  userId,
  messageIds,
}: {
  chatId: string;
  messageIds: string[];
  companyId: string;
  userId: string;
}) => {
  const fn = callable('markMessagesAsRead');
  const result = await fn({ chatId, messageIds, companyId, userId });
  return result.data;
};

export const sendMessageRequest = async ({
  chatId,
  sender,
  text,
  companyId,
  replyingMessageId,
  forwardedFrom,
  images = [],
  documents = [],
}: {
  chatId: string;
  sender: ChatUser;
  text: string;
  images?: ChatMessageAttachment[];
  documents?: ChatMessageAttachment[];
  companyId: string;
  replyingMessageId?: string;
  forwardedFrom?: { chatId: string; messageId: string; chatName: string };
}) => {
  const fn = callable('sendMessage');

  const result = await fn({
    chatId,
    sender,
    text,
    images,
    documents,
    companyId,
    replyTo: replyingMessageId,
    forwardedFrom,
  });

  return result.data;
};

export const createChatRequest = async ({
  creator,
  profiles,
  name,
  companyId,
  type,
}: {
  creator: ChatUser;
  profiles: ChatUser[];
  name: string;
  companyId: string;
  type: 'private' | 'group';
}) => {
  const fn = callable('createChat');

  const result = await fn({
    creator,
    profiles,
    name,
    companyId,
    type,
  });

  return result.data as { chatId: string };
};

export const searchCompanyChatUsersRequest = async ({
  companyId,
  searchQuery,
  limit,
}: {
  companyId: string;
  searchQuery: string;
  limit: number;
}) => {
  const fn = callable('searchCompanyChatUsers');
  const result = await fn({ companyId, searchQuery, limit });
  return result.data as { users: ChatUser[] };
};

export const syncCompanyChatsRequest = async ({
  companyId,
  companyName,
  creator,
  teams = [],
  locations = [],
  token,
}: {
  companyId: string;
  companyName: string;
  creator: ChatUser;
  teams: { name: string; id: string; description: string }[];
  locations: { name: string; id: string; description: string }[];
  token: string;
}) => {
  return axios.post(
    firebaseFunctionUrl('createcompanychats'),
    {
      companyId,
      companyName,
      creator,
      teams,
      locations,
    },
    { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } }
  );
};

export const syncCompanyChatsUsersRequest = async (companyId: string) => {
  return coreApiAxios.post(
    `/service/chats/companies/${companyId}/sync`,
    {},
    {
      headers: {
        'service-token': config.serviceToken,
      },
    }
  );
};

export const deleteMessageRequest = async ({
  chatId,
  messageId,
  companyId,
  userId,
}: {
  chatId: string;
  messageId: string;
  companyId: string;
  userId: string;
}) => {
  const fn = callable('deleteMessage');
  const result = await fn({ companyId, chatId, messageId, userId });
  return result.data;
};

export const editMessageRequest = async ({
  chatId,
  messageId,
  companyId,
  userId,
  newText,
}: {
  chatId: string;
  messageId: string;
  companyId: string;
  userId: string;
  newText: string;
}) => {
  const fn = callable('editMessage');
  const result = await fn({ companyId, chatId, messageId, userId, newText });
  return result.data;
};

export const reactToMessageRequest = async ({
  companyId,
  chatId,
  messageId,
  user,
  emoji,
  action,
}: {
  chatId: string;
  messageId: string;
  companyId: string;
  user: ChatUser;
  emoji: string;
  action: 'add' | 'remove';
}) => {
  const fn = callable('reactToMessage');
  const result = await fn({ chatId, messageId, companyId, user, emoji, action });
  return result.data;
};

export const getMessageReactionsRequest = async ({
  companyId,
  chatId,
  messageId,
  userId,
}: {
  companyId: string;
  chatId: string;
  messageId: string;
  userId: string;
}) => {
  const fn = callable('getMessageReactions');
  const result = await fn({ companyId, chatId, messageId, userId });
  return result.data as { reactions: ChatMessageReaction[] };
};

export const ensureUserExistsRequest = async (user: ChatUser) => {
  const fn = callable('ensureUserExists');
  const result = await fn({ user });
  return result.data;
};
